/**
 * 🗄️ Databases Page — galeria de bases de dados (listagem) + shell de registros.
 *
 * - Sem base selecionada: galeria das bases (toolbar 3 zonas · busca · filtro por tipo
 *   via typebar de pills · view Lista/Cards · seleção + bulk delete) ou empty-state.
 * - Base selecionada (`:id`): header com breadcrumb + seletor de base + Configurações,
 *   e o conteúdo de registros por tipo (Properties/Products/Documents).
 */

import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  ContextSelector,
  ListPageEmpty,
  ListPageLoading,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  ToolbarSearch,
  ViewToggle,
} from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Icon } from '@/shared/icons';
import type { DatabaseResponse, DatabaseType } from '@troia-v3/types';
import { CreateDatabaseModal } from '../components/CreateDatabaseModal';
import { DatabaseSettingsModal } from '../components/DatabaseSettingsModal';
import { DatabaseGalleryCard } from '../components/DatabaseGalleryCard';
import { DatabaseGalleryRow } from '../components/DatabaseGalleryRow';
import { DATABASE_TYPE_ORDER, DATABASE_TYPE_META } from '../components/databaseTypeMeta';
import { PropertiesContent } from '../components/content/PropertiesContent';
import { ProductsContent } from '../components/content/ProductsContent';
import { DocumentsContent } from '../components/content/DocumentsContent';
import { useDatabases, useDatabase, useDeleteDatabase } from '../hooks/useDatabases';

const DatabaseTypePlaceholder: React.FC<{ type?: string }> = ({ type }) => (
  <div className="h-full flex items-center justify-center px-6">
    <div className="text-center">
      <p className="text-base font-semibold" style={{ color: 'var(--theme-card-text)' }}>
        Tipo de base em breve
      </p>
      <p className="text-sm mt-1" style={{ color: 'var(--theme-muted-text)' }}>
        A visualização para bases do tipo "{type}" ainda não está disponível.
      </p>
    </div>
  </div>
);

const renderDatabaseContent = (type: DatabaseType | undefined, databaseId: string) => {
  switch (type) {
    case 'products':
      return <ProductsContent databaseId={databaseId} />;
    case 'documents':
      return <DocumentsContent databaseId={databaseId} />;
    case 'properties':
      return <PropertiesContent databaseId={databaseId} />;
    default:
      // vehicles/services (e futuros) NÃO caem em PropertiesContent — placeholder.
      return <DatabaseTypePlaceholder type={type} />;
  }
};

export const DatabasesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  // Galeria
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modais
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [settingsDbId, setSettingsDbId] = useState<string | null>(null);

  const { data: databasesData, isLoading } = useDatabases({ page: 1, limit: 100 });
  const { data: database } = useDatabase(id || '');
  const deleteDatabase = useDeleteDatabase();

  const databases = useMemo<DatabaseResponse[]>(() => databasesData?.items ?? [], [databasesData]);

  // Itens do seletor de base (ContextSelector padrão .funnel — só nome, sem legenda/avatar).
  // Fallback: se a base atual não está na lista (paginação), usa o doc já carregado.
  const selectorItems = useMemo(() => {
    const items = databases.map((db) => ({ id: db.id, name: db.name }));
    if (id && database && !items.some((i) => i.id === id)) {
      items.unshift({ id: database.id, name: database.name });
    }
    return items;
  }, [databases, id, database]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    databases.forEach((db) => { counts[db.type] = (counts[db.type] ?? 0) + 1; });
    return counts;
  }, [databases]);

  const presentTypes = useMemo(
    () => DATABASE_TYPE_ORDER.filter((t) => (typeCounts[t] ?? 0) > 0),
    [typeCounts],
  );

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return databases.filter((db) => {
      if (typeFilter && db.type !== typeFilter) return false;
      if (term && !`${db.name} ${db.description ?? ''}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [databases, typeFilter, searchTerm]);

  const selectedCount = selectedIds.size;
  const allSelected = filtered.length > 0 && filtered.every((db) => selectedIds.has(db.id));
  const someSelected = filtered.some((db) => selectedIds.has(db.id));

  const openDatabase = (dbId: string) => navigate(`/databases/${dbId}`);

  const toggleSelect = (dbId: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(dbId)) next.delete(dbId); else next.add(dbId);
      return next;
    });

  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach((db) => next.delete(db.id));
      else filtered.forEach((db) => next.add(db.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleDeleteDatabase = async (dbId: string) => {
    if (!(await confirm({ message: 'Excluir esta base de dados? Todos os registros serão perdidos.', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    deleteDatabase.mutate(dbId);
    setSelectedIds((prev) => { const next = new Set(prev); next.delete(dbId); return next; });
    if (dbId === id) navigate('/databases');
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} base(s) de dados selecionada(s)? Todos os registros serão perdidos.`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    const ids = Array.from(selectedIds);
    ids.forEach((dbId) => deleteDatabase.mutate(dbId));
    clearSelection();
    if (id && ids.includes(id)) navigate('/databases');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // VIEW: registros de uma base (`:id`)
  // ─────────────────────────────────────────────────────────────────────────
  if (id) {
    return (
      <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        {/* Toolbar 3 zonas — centro omitido: a busca de registros vive no conteúdo por tipo */}
        <PageToolbar
          left={<>
            {/* Esquerda: breadcrumb + seletor de base */}
            <button
              onClick={() => navigate('/databases')}
              className="text-sm font-medium truncate hidden sm:inline"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="databases-breadcrumb"
            >
              Bases de dados
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline flex-none" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            {/* Seletor de base — ContextSelector padrão (F87): título bold + chevron,
                mesma anatomia dos seletores de funil (CRM) e pipeline (Help Desk) */}
            <ContextSelector
              items={selectorItems}
              selectedId={id}
              onSelect={openDatabase}
              placeholder="Selecionar base"
              data-testid="database-selector"
              footer={
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(true)}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-ds-base font-medium text-left"
                  style={{ color: '#8b5cf6' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  data-testid="database-selector-new"
                >
                  <Icon name="plus" className="w-4 h-4" />
                  Nova base de dados
                </button>
              }
            />
          </>}
          right={
            <Button
              variant="secondary"
              icon="settings"
              collapsibleLabel
              className="flex-none"
              onClick={() => setSettingsDbId(id)}
              title="Configurar base de dados"
              data-testid="database-settings"
            >
              Configurações
            </Button>
          }
        />

        {/* Conteúdo de registros por tipo */}
        <div className="flex-1 min-h-0">
          {database ? renderDatabaseContent(database.type, id) : <ListPageLoading message="Carregando base…" />}
        </div>

        <CreateDatabaseModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={(db) => {
            setIsCreateModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['databases'] });
            navigate(`/databases/${db.id}`);
          }}
        />
        {settingsDbId && (
          <DatabaseSettingsModal
            isOpen
            onClose={() => setSettingsDbId(null)}
            databaseId={settingsDbId}
          />
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // VIEW: galeria de bases (sem `:id`)
  // ─────────────────────────────────────────────────────────────────────────
  const hasDatabases = databases.length > 0;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Toolbar 3 zonas */}
      <PageToolbar
        left={<>
          {/* Esquerda: título + contador */}
          <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Bases de dados</span>
          {hasDatabases && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {databases.length}
            </span>
          )}
        </>}
        center={hasDatabases && (
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar base de dados…"
              data-testid="databases-search"
            />
          )}
        right={<>
          {/* Direita: view toggle + Nova base */}
          {hasDatabases && (
            <ViewToggle
              modes={[
                { id: 'table', icon: 'list', label: 'Lista' },
                { id: 'grid', icon: 'grid', label: 'Cards' },
              ]}
              value={viewMode}
              onChange={setViewMode}
              data-testid="databases-view"
            />
          )}

          {/* Nova base (único primário — Button shared, F01-03) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => setIsCreateModalOpen(true)}
            data-testid="new-database"
          >
            Nova base
          </Button>
        </>}
      />

      {/* Typebar (pills por tipo) — filtro por tipo estilo mockup .db-typebar/.tpill */}
      {hasDatabases && (
        <div className="flex-none flex items-center flex-wrap gap-2" style={{ padding: '16px 18px 4px' }} data-testid="databases-typebar">
          <button
            onClick={() => setTypeFilter(null)}
            className="inline-flex items-center gap-[7px] h-8 px-[13px] rounded-full font-semibold transition-colors"
            style={{
              fontSize: '12.5px',
              border: !typeFilter ? '1px solid transparent' : '1px solid var(--theme-input-border)',
              backgroundColor: !typeFilter ? 'var(--theme-primary-background)' : 'var(--theme-card-background)',
              color: !typeFilter ? 'var(--theme-primary)' : 'var(--theme-card-text)',
            }}
            data-testid="databases-typepill-all"
          >
            <Icon name="database" className="w-4 h-4 flex-none" />
            Todas
            <span style={{ fontSize: '11px', opacity: 0.7 }}>{databases.length}</span>
          </button>
          {presentTypes.map((t) => {
            const active = typeFilter === t;
            return (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className="inline-flex items-center gap-[7px] h-8 px-[13px] rounded-full font-semibold transition-colors"
                style={{
                  fontSize: '12.5px',
                  border: active ? '1px solid transparent' : '1px solid var(--theme-input-border)',
                  backgroundColor: active ? 'var(--theme-primary-background)' : 'var(--theme-card-background)',
                  color: active ? 'var(--theme-primary)' : 'var(--theme-card-text)',
                }}
                data-testid={`databases-typepill-${t}`}
              >
                <Icon name={DATABASE_TYPE_META[t].icon} className="w-4 h-4 flex-none" />
                {DATABASE_TYPE_META[t].label}
                <span style={{ fontSize: '11px', opacity: 0.7 }}>{typeCounts[t] ?? 0}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Bulk bar */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionada(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="databases-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }}>Cancelar</button>
        </div>
      )}

      {/* Conteúdo */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {isLoading && <ListPageLoading message="Carregando bases…" />}

          {/* Empty-state global (nenhuma base) */}
          {!isLoading && !hasDatabases && (
            <div className="flex flex-col items-center text-center py-10 gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: 'var(--theme-primary-background)' }}>
                <Icon name="database" className="w-8 h-8" style={{ color: 'var(--theme-primary)' }} />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: 'var(--theme-card-text)' }}>Bases de Dados</h2>
                <p className="text-sm mt-1 max-w-md" style={{ color: 'var(--theme-muted-text)' }}>
                  Centralize catálogos de imóveis, produtos, serviços, veículos e documentos. Conecte-os a agentes de IA, funis, workflows e sites.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full max-w-lg mt-1 text-left">
                {DATABASE_TYPE_ORDER.map((t) => {
                  const m = DATABASE_TYPE_META[t];
                  return (
                    <div key={t} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center flex-none" style={{ backgroundColor: 'var(--theme-primary-background)' }}>
                        <Icon name={m.icon} className="w-4 h-4" style={{ color: 'var(--theme-primary)' }} />
                      </span>
                      <div className="min-w-0">
                        <div className="font-bold text-sm" style={{ color: 'var(--theme-card-text)' }}>{m.label}</div>
                        <div className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>{m.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="mt-2 inline-flex items-center gap-2 h-10 px-5 rounded-lg text-sm font-semibold text-white transition-colors"
                style={{ backgroundColor: 'var(--theme-button-primary)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-primary-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-primary)'; }}
                data-testid="new-database-empty"
              >
                <Icon name="plus" className="w-4 h-4" />
                Criar Base de Dados
              </button>
            </div>
          )}

          {/* Empty após filtro/busca */}
          {!isLoading && hasDatabases && filtered.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhuma base encontrada"
              description="Tente ajustar a busca ou o filtro de tipo."
            />
          )}

          {/* Cards — sem checkbox (F34: seleção em massa só na view Lista) */}
          {!isLoading && hasDatabases && filtered.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((db) => (
                <DatabaseGalleryCard
                  key={db.id}
                  database={db}
                  onOpen={openDatabase}
                  onSettings={setSettingsDbId}
                  onDelete={handleDeleteDatabase}
                />
              ))}
              {/* Card "nova base" */}
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="rounded-xl flex flex-col items-center justify-center gap-2 min-h-[150px] transition-colors"
                style={{ border: '1.5px dashed var(--theme-input-border)', color: 'var(--theme-muted-text)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--theme-primary)'; e.currentTarget.style.color = 'var(--theme-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--theme-input-border)'; e.currentTarget.style.color = 'var(--theme-muted-text)'; }}
                data-testid="new-database-card"
              >
                <span className="w-10 h-10 rounded-[11px] flex items-center justify-center" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
                  <Icon name="plus" className="w-5 h-5" />
                </span>
                <span className="text-sm font-semibold">Nova base de dados</span>
              </button>
            </div>
          )}

          {/* Lista */}
          {!isLoading && hasDatabases && filtered.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {filtered.map((db) => (
                  <DatabaseGalleryCard
                    key={db.id}
                    database={db}
                    onOpen={openDatabase}
                    onSettings={setSettingsDbId}
                    onDelete={handleDeleteDatabase}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onToggle={toggleSelectAll}
                        data-testid="databases-select-all"
                      />
                      <PlainHeadCell label="Base" />
                      <PlainHeadCell label="Tipo" />
                      <PlainHeadCell label="Registros" />
                      <PlainHeadCell label="Status" />
                      <PlainHeadCell label="Atualizado" />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((db) => (
                      <DatabaseGalleryRow
                        key={db.id}
                        database={db}
                        selected={selectedIds.has(db.id)}
                        onOpen={openDatabase}
                        onToggleSelect={toggleSelect}
                        onSettings={setSettingsDbId}
                        onDelete={handleDeleteDatabase}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modais */}
      <CreateDatabaseModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(db) => {
          setIsCreateModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ['databases'] });
          navigate(`/databases/${db.id}`);
        }}
      />
      {settingsDbId && (
        <DatabaseSettingsModal
          isOpen
          onClose={() => setSettingsDbId(null)}
          databaseId={settingsDbId}
        />
      )}
    </div>
  );
};
